import React, { useCallback, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form-7";
import useNotification from "../../hooks/useNotification";
import useLocalizedOptionsConfiguration from "../../hooks/useLocalizedOptionsConfiguration";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import SkillSearchPopover from "../skills/SkillSearchPopover";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  VStack,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  SkillFragment,
  useInsertAnswer,
  useCreateQuestion,
} from "../../graphql/generated/definitions";

type AddUserSkillModalProps = {
  open: boolean;
  onClose: VoidFunction;
  userId: string;
  refetch;
};

const AddUserSkillModal: React.FC<AddUserSkillModalProps> = ({
  open,
  onClose,
  userId,
  refetch,
}) => {
  const t = useTranslations();
  const {
    id: viewerId,
    company: { id: companyId },
  } = useViewer();

  const options = useLocalizedOptionsConfiguration();

  const { setValue, register, reset, control, handleSubmit } = useForm({});

  const selectorWatch = useWatch({
    control: control,
    name: `value.selector`,
  });

  const skillWatch = useWatch({
    control: control,
    name: `skill`,
  });

  const handleItemSelected = useCallback(
    (item: SkillFragment) => {
      setValue(`label`, item.name);
      setValue(`skillId`, item.id);
      setValue(`skill`, item);
      setValue(`type`, "QuestionSkill");
    },
    [setValue]
  );

  const { notifySuccess, notifyError } = useNotification();

  const [createQuestion] = useCreateQuestion();
  const [insertAnswer] = useInsertAnswer();

  const onSubmit = useCallback(
    async (data) => {
      const {
        data: {
          createQuestion: { question },
        },
      } = await createQuestion({
        variables: {
          input: {
            label: data.label,
            type: "QuestionSkill",
            companyId,
            skillId: data.skillId,
            options: options.skillQuestionOptionsConfiguration,
          },
        },
      });
      try {
        await insertAnswer({
          variables: {
            input: {
              questionId: question.id,
              userId: viewerId,
              collaboratorId: userId,
              value: data.value,
              type: "AnswerSkill",
            },
          },
        });
        notifySuccess({
          title: t("users.skills.notification.success.add.title") as string,
          description: t(
            "users.skills.notification.success.add.body"
          ) as string,
        });
      } catch {
        notifyError({
          title: t("users.skills.notification.error.title") as string,
        });
      }
      onClose();
      refetch();
    },
    [createQuestion]
  );

  useEffect(() => {
    reset();
  }, [open]);

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)} id="questionForm">
          <ModalHeader>{t("users.skills.add")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={8}>
              <SkillSearchPopover handleItemSelected={handleItemSelected} />

              <FormControl>
                <FormLabel>{t("users.skills.selectGrade")}</FormLabel>
                <RadioGroup>
                  <VStack align="stretch">
                    {options.skillQuestionOptionsConfiguration.map(
                      ({ value, label }) => (
                        <React.Fragment key={value}>
                          <Radio
                            data-test-id={`radio_selectGrade_${value}`}
                            value={value}
                            isDisabled={!skillWatch}
                            {...register(`value.selector`)}
                          >
                            {label}
                          </Radio>
                          <Spacer />
                        </React.Fragment>
                      )
                    )}
                  </VStack>
                </RadioGroup>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button id="closeAddUserSkill" onClick={onClose}>
                {t("forms.actions.cancel")}
              </Button>

              <Spacer />

              <Button
                disabled={!selectorWatch}
                id="addUserSkill"
                type="submit"
                form="questionForm"
                colorScheme="blue"
              >
                {t("forms.actions.save")}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(AddUserSkillModal);
